import {useEffect, useState, useCallback, useRef} from 'react';

const INITIAL_STATE = {
    list: [],
    searchPlaceHolder: 'Search input',
    Icon: null,
    position: {x: 0, y: 0},
    handleSelect: () => {},
    handleNoResult: null,
    noResultText: 'No result found',
    isMultipleChoices: false,
    defaultValue: null,
    initialSelected: null,
    fetchData: null,
    renderItem: null,
    showActionBar: false,
    onSave: null,
    onCancel: null,
    title: null,
    totalSelected: null,
    pageSize: 30,
    anchorEl: null,
    headerAction: null,
};

const sortByLabel = (items) => [...items].sort((a, b) => a.label.localeCompare(b.label));

const filterBySearch = (items, query) => {
    const normalized = query.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return items.filter((item) => {
        const label = item.label?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        return label.includes(normalized);
    });
};

export const useSelectModalState = (modalStore) => {
    const [searchString, setSearchString] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [data, setData] = useState(INITIAL_STATE);
    const [list, setList] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const searchTimerRef = useRef(null);

    const isServerMode = !!data.fetchData;

    const resetData = () => {
        setSearchString('');
        setIsSearching(false);
        setSelectedOptions([]);
        setData(INITIAL_STATE);
        setList([]);
        setHasMore(false);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };

    const closeModal = () => {
        const shouldCallback = data.isMultipleChoices && !data.showActionBar;
        const prevData = shouldCallback ? {...data} : null;
        const prevSelected = shouldCallback ? [...selectedOptions] : null;
        modalStore.isGenericSelectSearchModalOpen = false;
        modalStore.modalData.genericSelectData = null;
        setTimeout(() => {
            resetData();
            if (shouldCallback && prevData) prevData.handleSelect(prevSelected);
        }, 300);
    };

    const handleCancel = () => {
        const onCancel = data.onCancel;
        modalStore.isGenericSelectSearchModalOpen = false;
        modalStore.modalData.genericSelectData = null;
        setTimeout(() => {
            resetData();
            if (onCancel) onCancel();
        }, 300);
    };

    const handleSave = () => {
        const onSave = data.onSave;
        const selected = [...selectedOptions];
        modalStore.isGenericSelectSearchModalOpen = false;
        modalStore.modalData.genericSelectData = null;
        setTimeout(() => {
            resetData();
            if (onSave) onSave(selected);
        }, 300);
    };

    const handleOnClick = (item, index) => {
        if (!data.isMultipleChoices) {
            data.handleSelect(item);
            return;
        }
        let newSelected = [...selectedOptions];
        if (data.defaultValue) {
            newSelected = newSelected.filter((i) => i !== data.defaultValue.value);
        }
        if (index === -1) {
            newSelected = data.defaultValue ? [data.defaultValue.value] : [];
        } else if (newSelected.includes(item.value)) {
            newSelected = newSelected.filter((i) => i !== item.value);
            if (data.defaultValue && newSelected.length === 0) {
                newSelected = [data.defaultValue.value];
            }
        } else {
            newSelected.push(item.value);
        }
        setSelectedOptions(newSelected);
    };

    const fetchServerData = useCallback(async (skip, search, reset = false) => {
        if (!data.fetchData) return;
        setIsFetching(true);
        try {
            const result = await data.fetchData(skip, data.pageSize, search || null);
            const newItems = result.data || [];
            setHasMore(skip + newItems.length < result.totalCount);
            setList((prev) => reset ? newItems : [...prev, ...newItems]);
            if (result.initialSelected) {
                setSelectedOptions((prev) => {
                    const existing = new Set(prev);
                    result.initialSelected.forEach((id) => existing.add(id));
                    return [...existing];
                });
            }
            if (data.onFetchComplete) data.onFetchComplete(result);
        } catch (error) {
            console.error('GenericSelectSearchModal fetch failed:', error);
        } finally {
            setIsFetching(false);
        }
    }, [data.fetchData, data.pageSize]);

    const handleServerSearch = useCallback((value) => {
        setSearchString(value);
        setList([]);
        setIsFetching(true);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            fetchServerData(0, value, true);
        }, 300);
    }, [fetchServerData]);

    const handleScroll = useCallback((e) => {
        const {scrollTop, scrollHeight, clientHeight} = e.target;
        if (scrollHeight - scrollTop - clientHeight < 50 && hasMore && !isFetching) {
            fetchServerData(list.length, searchString);
        }
    }, [hasMore, isFetching, list.length, searchString, fetchServerData]);

    const handleSearch = () => {
        setIsSearching(true);
        const query = searchString.trim().toLowerCase();
        if (!query) {
            setList(sortByLabel(data.list));
            setIsSearching(false);
            return;
        }
        setList(sortByLabel(filterBySearch(data.list, query)));
        setIsSearching(false);
    };

    const initializeModal = () => {
        const selectData = modalStore.modalData.genericSelectData;
        if (!selectData?.position && !selectData?.anchorEl) return;
        setData({...INITIAL_STATE, ...selectData});
        if (!selectData.fetchData) {
            setList(sortByLabel(selectData.list));
        }
        if (selectData.initialSelected && selectData.isMultipleChoices) {
            setSelectedOptions(selectData.initialSelected);
        }
    };

    useEffect(() => {
        if (modalStore.isGenericSelectSearchModalOpen) initializeModal();
    }, [modalStore.isGenericSelectSearchModalOpen]);

    useEffect(() => {
        if (isServerMode && modalStore.isGenericSelectSearchModalOpen) {
            fetchServerData(0, '', true);
        }
    }, [isServerMode, modalStore.isGenericSelectSearchModalOpen]);

    useEffect(() => {
        if (!isServerMode) {
            if (searchString.length > 0) {
                handleSearch();
            } else {
                setList(sortByLabel(data.list));
            }
        }
    }, [searchString]);

    useEffect(() => {
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        };
    }, []);

    return {
        data,
        list,
        searchString,
        setSearchString,
        selectedOptions,
        isServerMode,
        isFetching,
        closeModal,
        handleCancel,
        handleSave,
        handleOnClick,
        handleServerSearch,
        handleScroll,
    };
};
